"use client";

import { MARKETING_LANDING_VIDEO_FULLSCREEN_CONTROLS_CLASSNAME } from "@marketing/shared/lib/marketing-landing-video-chrome-layout";
import {
	MARKETING_LANDING_VIDEO_PLAYER_OVERLAY_CHIP_CLASSNAME,
	MARKETING_LANDING_VIDEO_UNMUTE_HINT_DISC_CLASSNAME,
	MARKETING_LANDING_VIDEO_UNMUTE_HINT_FRAME_CLASSNAME,
	MARKETING_LANDING_VIDEO_UNMUTE_HINT_ICON_CLASSNAME,
	MARKETING_LANDING_VIDEO_UNMUTE_PULSE_RING_BORDER_DARK,
	MARKETING_LANDING_VIDEO_UNMUTE_PULSE_RING_BORDER_LIGHT,
	MARKETING_LANDING_VIDEO_UNMUTE_PULSE_RING_LAYOUT,
} from "@marketing/shared/lib/marketing-landing-video-overlay-chrome";
import { MARKETING_HEADER_CHROME_PORTAL_ATTR } from "@marketing/shared/lib/marketing-site-header-outside-interaction";
import { cn } from "@repo/ui";
import { PulseRing } from "@repo/ui/components/pulse-ring";
import { Fullscreen, Minimize, Volume2, VolumeX, X } from "lucide-react";
import { useTheme } from "next-themes";
import type { SyntheticEvent } from "react";
import { useEffect, useState } from "react";
import { useMarketingLandingVideoOverlay } from "./marketing-landing-video-overlay-context";

interface MarketingLandingVideoOverlayControlsProps {
	surface: "navbar" | "fullscreen";
}

function stopHeaderChromeEventPropagation(event: SyntheticEvent): void {
	event.stopPropagation();
}

function MarketingLandingVideoOverlayControls({
	surface,
}: MarketingLandingVideoOverlayControlsProps) {
	const { resolvedTheme } = useTheme();
	const [hasClientTheme, setHasClientTheme] = useState(false);
	useEffect(() => {
		setHasClientTheme(true);
	}, []);

	const {
		muted,
		setMuted,
		hasUserUnmutedOnce,
		isFullscreen,
		toggleFullscreen,
		requestClose,
	} = useMarketingLandingVideoOverlay();

	const unmutePulseRingBorderColor =
		hasClientTheme && resolvedTheme === "dark"
			? MARKETING_LANDING_VIDEO_UNMUTE_PULSE_RING_BORDER_DARK
			: MARKETING_LANDING_VIDEO_UNMUTE_PULSE_RING_BORDER_LIGHT;

	const FullscreenIcon = isFullscreen ? Minimize : Fullscreen;
	const chipClassName = cn(
		MARKETING_LANDING_VIDEO_PLAYER_OVERLAY_CHIP_CLASSNAME,
		surface === "navbar" && "shadow-sm",
	);

	return (
		<div
			className={cn(
				surface === "navbar"
					? "flex items-center justify-center gap-2 sm:gap-3"
					: MARKETING_LANDING_VIDEO_FULLSCREEN_CONTROLS_CLASSNAME,
			)}
			{...(surface === "navbar"
				? { [MARKETING_HEADER_CHROME_PORTAL_ATTR]: "" }
				: {})}
			onPointerDown={
				surface === "navbar"
					? stopHeaderChromeEventPropagation
					: undefined
			}
		>
			<button
				type="button"
				onPointerDown={
					surface === "navbar"
						? stopHeaderChromeEventPropagation
						: undefined
				}
				onClick={(event) => {
					if (surface === "navbar") {
						stopHeaderChromeEventPropagation(event);
					}
					setMuted((current) => !current);
				}}
				className={cn(
					chipClassName,
					"isolate relative inline-flex shrink-0 items-center justify-center overflow-visible",
					muted &&
						!hasUserUnmutedOnce &&
						cn(
							"group",
							MARKETING_LANDING_VIDEO_UNMUTE_HINT_FRAME_CLASSNAME,
						),
				)}
				aria-label={muted ? "Unmute video" : "Mute video"}
			>
				{muted && !hasUserUnmutedOnce ? (
					<>
						<span
							aria-hidden
							className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center"
						>
							<span className="relative block size-[44px] shrink-0">
								<PulseRing
									className="absolute inset-0"
									{...MARKETING_LANDING_VIDEO_UNMUTE_PULSE_RING_LAYOUT}
									borderColor={unmutePulseRingBorderColor}
								/>
							</span>
						</span>
						<span
							aria-hidden
							className={
								MARKETING_LANDING_VIDEO_UNMUTE_HINT_DISC_CLASSNAME
							}
							style={{
								animationDuration: `${MARKETING_LANDING_VIDEO_UNMUTE_PULSE_RING_LAYOUT.durationS}s`,
							}}
						/>
					</>
				) : null}
				<span
					className={cn(
						"relative z-[2] inline-flex items-center justify-center transition-colors duration-300 ease-out",
						muted &&
							!hasUserUnmutedOnce &&
							"group-hover:text-neutral-800 dark:group-hover:text-neutral-800",
					)}
				>
					{muted ? (
						<VolumeX
							className={cn(
								"size-4",
								!hasUserUnmutedOnce &&
									MARKETING_LANDING_VIDEO_UNMUTE_HINT_ICON_CLASSNAME,
							)}
							aria-hidden
						/>
					) : (
						<Volume2 className="size-4" aria-hidden />
					)}
				</span>
			</button>
			<button
				type="button"
				onPointerDown={
					surface === "navbar"
						? stopHeaderChromeEventPropagation
						: undefined
				}
				onClick={(event) => {
					if (surface === "navbar") {
						stopHeaderChromeEventPropagation(event);
					}
					void toggleFullscreen();
				}}
				className={chipClassName}
				aria-label={
					isFullscreen ? "Exit fullscreen" : "Enter fullscreen"
				}
			>
				<FullscreenIcon className="size-4" aria-hidden />
			</button>
			<button
				type="button"
				onPointerDown={
					surface === "navbar"
						? stopHeaderChromeEventPropagation
						: undefined
				}
				onClick={(event) => {
					if (surface === "navbar") {
						stopHeaderChromeEventPropagation(event);
					}
					requestClose();
				}}
				className={chipClassName}
				aria-label="Close video"
			>
				<X className="size-4" aria-hidden />
			</button>
		</div>
	);
}

export function MarketingLandingVideoNavChips() {
	return <MarketingLandingVideoOverlayControls surface="navbar" />;
}

export function MarketingLandingVideoFullscreenControls() {
	return <MarketingLandingVideoOverlayControls surface="fullscreen" />;
}
